// MasterQuizList.jsx
import React, { useEffect, useState } from "react";
import StudentLeftside from "./StudentLeftside";
import ViewResult from "./ViewResult";
import "./MasterQuizList.css";
import Swal from "sweetalert2";
import API_BASE_URL from "../config/api";

const MASTER_LIST_ENDPOINT = `${API_BASE_URL}/master_quiz_list.php`;
const SUBJECT_API = `${API_BASE_URL}/book_categories.php`;
const PAGE_SIZE = 6;

// date formatter
const formatStartTime = (raw) => {
  if (!raw) return "—";
  const d = new Date(raw.replace(" ", "T"));
  if (isNaN(d)) return raw;
  return d.toLocaleString("en-IN", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};

const MasterQuizList = () => {
  const [rows, setRows] = useState([]);
  const [subjects, setSubjects] = useState([]);  
  const [subjectId, setSubjectId] = useState("");  

  const [page, setPage] = useState(1);
  const [totalRows, setTotalRows] = useState(0);
  const [filter, setFilter] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [modalOpen, setModalOpen] = useState(false);
  const [selectedExamStartId, setSelectedExamStartId] = useState(null);

  /* ================= LOGIN CHECK ================= */
  useEffect(() => {
    const user = JSON.parse(sessionStorage.getItem("user"));
    if (!user?.id) {
      Swal.fire("Not Logged In", "Please login first", "warning").then(() => {
        window.location.href = "/login";
      });
    }
  }, []);

  /* ================= FETCH SUBJECTS ================= */
  useEffect(() => {
    const fetchSubjects = async () => {
      try {
        const res = await fetch(SUBJECT_API);
        const data = await res.json();
        if (data.success && Array.isArray(data.categories)) {
          setSubjects(data.categories);
        }
      } catch (err) {
        console.error("Subject fetch error", err);
      }
    };
    fetchSubjects();
  }, []);

  /* ================= FETCH QUIZ LIST ================= */
  useEffect(() => {
    const user = JSON.parse(sessionStorage.getItem("user"));
    if (!user?.id) return;

    const controller = new AbortController();

    const fetchList = async () => {
      setLoading(true);
      setError("");

      try {
        const params = new URLSearchParams({
          user_id: user.id,
          page,
          page_size: PAGE_SIZE,
        });

        if (filter.trim()) params.append("q", filter.trim());
        if (subjectId) params.append("subject_id", subjectId); // ✅ IMPORTANT

        const res = await fetch(
          `${MASTER_LIST_ENDPOINT}?${params.toString()}`,
          { signal: controller.signal }
        );

        const data = await res.json();
        if (!data.success) throw new Error(data.message);

        setRows(data.items || []);
        setTotalRows(Number(data.total) || 0);
      } catch (err) {
        if (err.name !== "AbortError") {
          setError(err.message || "Failed to load data");
          setRows([]);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchList();
    return () => controller.abort();
  }, [page, filter, subjectId]);

  const totalPages = Math.max(1, Math.ceil(totalRows / PAGE_SIZE));

  return (
    <div className="dashboard-wrap bg-light min-h-screen flex">
      <StudentLeftside />

      <main className="dashboard-content container-fluid py-4 flex-1">

        {/* HEADER */}
       <div
  className="d-flex justify-content-between align-items-center mb-3 pt-8">

          <h3 className="mb-0">Master Quizzes</h3>

          {/* FILTERS */}
          <div className="gap-2 align-items-center item-shorting-box">

            {/* SUBJECT DROPDOWN (DYNAMIC) */}
            <select
              className="form-select w-auto"
              value={subjectId}
              onChange={(e) => {
                setSubjectId(e.target.value);
                setPage(1);
              }}
            >
              <option value="">All Subjects</option>
              {subjects.map((s) => (
                <option key={s.category_id} value={s.category_id}>
                  {s.category_name}
                </option>
              ))}
            </select>

            {/* TEXT FILTER */}
            <input 
              type="text"
              placeholder="Search by keyword / start id"
              className="form-control w-auto input-height"
              value={filter}
              onChange={(e) => {
                setFilter(e.target.value);
                setPage(1);
              }}
            />
          </div>
        </div>

        {loading && <div className="alert alert-info">Loading...</div>}
        {error && <div className="alert alert-danger">{error}</div>}

        {/* TABLE */}
        <div className="table-responsive">
          <table className="table table-bordered table-striped align-middle text-center">
            <thead className="table-primary">
              <tr>
                <th>No.</th>
                <th>Subject</th>
                <th>Start Time</th>
                <th>Duration</th>
                <th>Attempted</th>
                <th>Marks</th>
                <th>View</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && !loading && (
                <tr>
                  <td colSpan="7" className="text-muted py-3">
                    No quizzes found
                  </td>
                </tr>
              )}

              {rows.map((r, i) => (
                <tr key={r.start_exam_id}>
                  <td>{(page - 1) * PAGE_SIZE + i + 1}</td>
                  <td><strong>{r.title || r.chapter_name}</strong></td>
                  <td>{formatStartTime(r.start_time)}</td>
                  <td>{r.duration || "—"}</td>
                  <td>{r.total_attempted || "—"}</td>
                  <td> {r.marks}
                      {r.total_attempted > 0 && (
                        <span className="text-muted small">
                          {" "} / {r.total_attempted}
                        </span>
                      )}</td>
                  <td>
                    <button
                      className="btn btn-link p-0"
                      onClick={() => {
                        setSelectedExamStartId(r.start_exam_id);
                        setModalOpen(true);
                      }}
                    >
                      <i className="bi bi-eye fs-5 text-primary"></i>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* PAGINATION */}
        <div className="d-flex justify-content-between mt-3">
          <div>
            Page <strong>{page}</strong> of{" "}
            <strong>{totalPages}</strong>
          </div>
          <div>
            <button className="btn btn-outline-secondary btn-sm me-1" onClick={() => setPage(1)} disabled={page === 1}>⏮</button>
            <button className="btn btn-outline-secondary btn-sm me-1" onClick={() => setPage(page - 1)} disabled={page === 1}>◀</button>
            <button className="btn btn-outline-secondary btn-sm me-1" onClick={() => setPage(page + 1)} disabled={page === totalPages}>▶</button>
            <button className="btn btn-outline-secondary btn-sm" onClick={() => setPage(totalPages)} disabled={page === totalPages}>⏭</button>
          </div>
        </div>

        {/* MODAL */}
        {modalOpen && selectedExamStartId && (
          <div className="modal-full-overlay">
            <div className="modal-full-content p-3 bg-white position-relative">
              <button className="btn-close" onClick={() => setModalOpen(false)} />
              <ViewResult examStartId={selectedExamStartId} />
            </div>
          </div>
        )}

      </main>
    </div>
  );
};

export default MasterQuizList;
